import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import Typography from '@mui/material/Typography'
import { LoadingButton } from '@mui/lab'
import Box from '@mui/material/Box'
import Visibility from '@mui/icons-material/VisibilityOutlined'
import VisibilityOff from '@mui/icons-material/VisibilityOffOutlined'
import { AuthFormBox } from './__styled'
import { Input } from '@/components'
import { validateEmail, validatePassword } from '@/utils/helper'
import { SVGS } from '@/assets/svgs'
import SVG from 'react-inlinesvg'
import { useTheme } from '@mui/material'

interface FormProps {
  error: string
  loading: boolean
  user: string
  onSubmit: (code: string, newPassword: string) => void
}

export default function ForgotForm2({ error, loading, user, onSubmit }: FormProps) {
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [editing, setEditing] = useState(false)
  const theme = useTheme()

  const formError = useMemo(() => {
    const error: Record<string, string> = {}
    if (!code.length) {
      error.code = 'Verification code is required'
    } else if (code.length !== 6) {
      error.code = 'Password should be 6 chars exactly.'
    }
    if (!password.length) {
      error.password = 'Password is required'
    } else if (password.length < 8) {
      error.password = 'Password is too short - should be 8 chars minimum.'
    } else if (!validatePassword(password)) {
      error.password =
        'Password can only contain Latin Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and One Special Case Character.'
    }
    if (password !== confirmPassword) {
      error.confirm = 'Passwords must match'
    }
    return error
  }, [code, password, confirmPassword])

  const onChangeCode = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value)
    setEditing(true)
  }

  const onChangePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value)
    setEditing(true)
  }

  const onChangeConfirmPassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value)
    setEditing(true)
  }

  const isFormValid = !formError.code && !formError.password && !formError.confirm

  const onConfirm = () => {
    if (isFormValid) onSubmit(code, password)
  }

  return (
    <AuthFormBox>
      <Typography
        fontWeight={700}
        fontSize={{ xs: 32, md: 36 }}
        color='#0f172a'
        sx={{ letterSpacing: '-0.02em', mb: 1.5 }}
      >
        Confirmation Code
      </Typography>
      <Typography
        variant='p'
        fontSize={16}
        color='#64748b'
        lineHeight={1.6}
        mt={'12px'}
        mb={3}
        style={{ whiteSpace: 'pre-line' }}
        mr={'80px'}
        sx={{
          [theme.breakpoints.down('md')]: {
            mr: '40px',
          },
        }}
      >
        Enter the confirmation code sent to your {validateEmail(user) ? 'email' : 'phone'}{' '}
        <Typography component='span' style={{ fontWeight: 700, color: '#0f172a' }}>
          {user}
        </Typography>
      </Typography>
      <Input
        value={code}
        placeholder='Confirmation Code*'
        onChange={onChangeCode}
        errorText={formError.code}
        isError={editing && !!formError.code}
        name='code'
      />
      <Input
        value={password}
        placeholder='Create Password*'
        onChange={onChangePassword}
        rightIcon={<SVG src={showPassword ? SVGS.VisibilityOffIcon : SVGS.VisibilityIcon} />}
        onToggleIcon={() => setShowPassword(!showPassword)}
        type={showPassword ? 'text' : 'password'}
        errorText={formError.password}
        isError={editing && !!formError.password}
        autoComplete={'off'}
      />
      <Input
        value={confirmPassword}
        placeholder='Confirm Password*'
        onChange={onChangeConfirmPassword}
        rightIcon={<SVG src={showConfirmPassword ? SVGS.VisibilityOffIcon : SVGS.VisibilityIcon} />}
        onToggleIcon={() => setShowConfirmPassword(!showConfirmPassword)}
        type={showConfirmPassword ? 'text' : 'password'}
        errorText={formError.confirm}
        isError={editing && !!formError.confirm}
        autoComplete={'off'}
      />
      {!loading && !!error && (
        <Box mt={1}>
          <Typography variant='labelLarge' color={'error.main'}>
            {error}
          </Typography>
        </Box>
      )}
      <LoadingButton
        variant='contained'
        size='large'
        fullWidth
        disabled={!isFormValid}
        onClick={onConfirm}
        loading={loading}
        sx={{
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          borderRadius: '12px',
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '16px',
          letterSpacing: '-0.01em',
          py: '14px',
          mt: 1,
          boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            background: 'linear-gradient(135deg, #5558e3 0%, #7c4ee8 100%)',
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 24px rgba(99, 102, 241, 0.4)',
          },
          '&:active': {
            transform: 'translateY(0px)',
          },
          '&:disabled': {
            background: '#e2e8f0',
            color: '#94a3b8',
            boxShadow: 'none',
          },
        }}
      >
        Confirm
      </LoadingButton>
      <Box mt={2} display='flex' justifyContent='center' alignItems={'center'} gap={'6px'}>
        <Typography variant='labelLarge' fontSize={15} color='#64748b' fontFamily={'Inter'}>
          Already have an account?
        </Typography>
        <Link to='/login' style={{ textDecoration: 'none' }}>
          <Typography
            variant='labelLarge'
            fontSize={15}
            sx={{
              color: '#6366f1',
              fontWeight: 600,
              transition: 'color 0.2s ease',
              '&:hover': {
                color: '#5558e3',
              },
            }}
          >
            Sign In
          </Typography>
        </Link>
      </Box>
    </AuthFormBox>
  )
}
